import React, { useEffect, useMemo, useState } from "react";
import { useLocalStorage } from "../../auth/useLocalStorage";
import { CommonService } from "../../services/CommonService";
import {
  Dialog,
  DialogTitle,
  DialogActions,
  ToggleButtonGroup,
  ToggleButton,
  TextField,
  Button,
  CircularProgress,
  Backdrop,
  List,
  ListItemButton,
  ListItemText,
  Radio,
  Divider,
  IconButton,
} from "@mui/material";
import PlaceIcon from "@mui/icons-material/Place";
import { Circle, GoogleMap, Marker, Polygon } from "@react-google-maps/api";
import { CustomerService } from "../../services/CustomerService";
import { Map } from "../Map";
import { useModalStore } from "../../context/modalStore";
import { usePosStore, initialState as posInitialState } from "../../context/posStore";
import {
  IProduct,
  TRequestSale,
  PaymentsMethods,
  ICustomer,
  IPayment,
  ICustomerDeliveryAddress,
} from "../../common/interfaces";
import { SaleService } from "../../services/SaleService";
import Swal from "sweetalert2";
import "@sweetalert2/theme-dark/dark.css";
import withReactContent from "sweetalert2-react-content";
import { v4 } from "uuid";
import { useCustomerDeliveryAddresses } from "../../hooks/useCustomerDeliveryAddresses";
import { useShippingCalculator, applyFreeShippingDiscount, getFreeShippingRadius } from "../../hooks/useShippingCalculator";
import { usePosConfig } from "../../hooks/usePosConfig";
import { useFreeShippingPerimeter } from "../../hooks/useFreeShippingPerimeter";
import { needsOrderFlow, toSalePayload } from "../../services/pricing";
const MySwal = withReactContent(Swal);

export const OrderOptions: React.FC = () => {
  const modalManager = useModalStore();
  const clearOrder = usePosStore((state) => state.clearOrder);
  const setCustomer = usePosStore((state) => state.setCustomer);
  const handleCancel = () => {
    // Cancelar no debe dejar el cliente identificado pegado a la siguiente orden.
    setCustomer(posInitialState.customer);
    modalManager.closeModal();
  };
  // const posCustomer = usePosStore((state) => state.customer);
  const order = modalManager.params?.order;
  const customer = modalManager.params?.customer;
  const pays = modalManager.params?.pays ?? [];

  console.log("Modal params:");
  console.dir(modalManager.params);

  const products = usePosStore((state) => state.products);
  const radioGroupRef = React.useRef<HTMLElement>(null);
  const [phone, setPhone] = React.useState("");
  const [address, setAddress] = React.useState("");
  const [isLocal, setIsLocal] = React.useState(false);
  const [name, setName] = React.useState("");
  const [dir2, setDir2] = React.useState("");
  const [shippedType, setShippedType] = React.useState("");
  const [showDir, setShowDir] = React.useState(false);
  const [location, setLocation] = React.useState();
  const [shipping, setShipping] = React.useState(0);
  const [time, setTime] = React.useState("");
  const [user, setUser] = useLocalStorage("user", null);
  const [disableSend, setDisableSend] = useState(false);
  const [previewAddress, setPreviewAddress] = React.useState<ICustomerDeliveryAddress | null>(null);

  // Un cliente identificado es cualquiera distinto del genérico "Público en General" (id 1).
  const isRealCustomer = Boolean(customer && customer.id !== 1);

  const { addresses, createAddress, cacheShippingCost, getCachedCost } =
    useCustomerDeliveryAddresses(isRealCustomer ? customer.id : undefined);
  const { calculateShipping, isLoaded: mapsLoaded } = useShippingCalculator();
  const { config: shippingConfig, loading: configLoading } = usePosConfig();
  const { points: perimeterPoints } = useFreeShippingPerimeter(user?.user?.warehouse?.id);

  // 'list': mostrar direcciones ya guardadas del cliente. 'map': capturar una nueva por el mapa.
  const [addressMode, setAddressMode] = useState<"list" | "map">("map");
  const [selectedAddressId, setSelectedAddressId] = useState<number | null>(null);
  const [newAddressName, setNewAddressName] = useState("");
  // Precio BASE (sin descuento) + distancia que <Map> ya calculó para la
  // cotización en vivo, para cachear exactamente ese valor sin recalcularlo.
  const [lastMapQuote, setLastMapQuote] = useState<{
    rawPrice: number;
    distanceMeters: number;
    durationSeconds: number;
  } | null>(null);

  // Precio BASE (sin descuento) + distancia/duración por dirección, obtenidos
  // una sola vez (cacheados o recién calculados). El descuento de envío
  // gratis NUNCA se guarda aquí: se deriva abajo (rowCosts) a partir de esto
  // + el total ACTUAL del pedido, para que si el carrito cambia después de
  // abrir esta lista, el envío gratis se reevalúe solo sin quedar pegado al
  // subtotal que había la primera vez que se calculó esta fila.
  const [rowMeta, setRowMeta] = useState<
    Record<number, { rawPrice: number; distanceMeters: number; durationSeconds?: number }>
  >({});
  const [calculatingIds, setCalculatingIds] = useState<Record<number, boolean>>({});
  const requestedCostIds = React.useRef<Set<number>>(new Set());

  const handleEntering = () => {
    if (radioGroupRef.current != null) {
      radioGroupRef.current.focus();
    }
  };

  // Cuando se muestra la lista, calcula (y cachea) el costo de envío de
  // cualquier dirección que aún no lo tenga registrado para esta sucursal.
  useEffect(() => {
    // Espera a que la config real de precios (config/pos) haya cargado: si se
    // usan los valores por default mientras tanto, el descuento de envío
    // gratis puede calcularse mal y, al no volver a correr, queda pegado.
    if (addressMode !== "list" || !user || configLoading) return;
    addresses.forEach((deliveryAddress) => {
      if (requestedCostIds.current.has(deliveryAddress.id)) return;

      const cached = getCachedCost(deliveryAddress, user.warehouse_id);
      if (cached !== undefined) {
        requestedCostIds.current.add(deliveryAddress.id);
        setRowMeta((prev) => ({
          ...prev,
          [deliveryAddress.id]: {
            rawPrice: cached.rawPrice,
            distanceMeters: cached.distanceMeters,
            durationSeconds: cached.durationSeconds,
          },
        }));
        return;
      }

      // Necesita Google Maps cargado para calcular; si aún no está listo,
      // no se marca como solicitada y este efecto vuelve a intentarlo
      // cuando mapsLoaded pase a true.
      if (!mapsLoaded) return;

      requestedCostIds.current.add(deliveryAddress.id);
      setCalculatingIds((prev) => ({ ...prev, [deliveryAddress.id]: true }));
      calculateShipping(
        { lat: Number(user.user.warehouse.lat), lng: Number(user.user.warehouse.lng) },
        { lat: Number(deliveryAddress.lat), lng: Number(deliveryAddress.lng) },
        shippingConfig
      )
        .then((result) => {
          setRowMeta((prev) => ({
            ...prev,
            [deliveryAddress.id]: {
              rawPrice: result.rawPrice,
              distanceMeters: result.distanceMeters,
              durationSeconds: result.durationSeconds,
            },
          }));
          // Se cachea el precio BASE (sin descuento) + la distancia, no el
          // precio final: el descuento depende del total de esta orden y no
          // debe quedar pegado a la dirección/sucursal para siempre.
          cacheShippingCost(
            deliveryAddress.id,
            user.warehouse_id,
            result.rawPrice,
            result.distanceMeters,
            result.durationSeconds
          );
        })
        .catch((err) => {
          console.error("No se pudo calcular el costo de envío", err);
        })
        .finally(() => {
          setCalculatingIds((prev) => ({ ...prev, [deliveryAddress.id]: false }));
        });
    });
  }, [addressMode, addresses, mapsLoaded, configLoading]);

  // Derivado, no estado: se recalcula solo cada vez que cambia el total del
  // pedido (ej. se agregan más productos al carrito mientras el modal de
  // domicilio ya estaba abierto), sin depender de que se vuelva a disparar
  // el useEffect de arriba (que solo corre una vez por dirección).
  const rowCosts = useMemo(() => {
    const orderTotal = Number(order.total ?? 0);
    const result: Record<number, number> = {};
    for (const [id, meta] of Object.entries(rowMeta)) {
      result[Number(id)] = applyFreeShippingDiscount(meta.rawPrice, meta.distanceMeters, shippingConfig, orderTotal);
    }
    return result;
  }, [rowMeta, order.total, shippingConfig]);

  const handleSelectAddress = (deliveryAddress: ICustomerDeliveryAddress) => {
    setSelectedAddressId(deliveryAddress.id);
    setAddress(deliveryAddress.address);
    setLocation({
      lat: Number(deliveryAddress.lat),
      lng: Number(deliveryAddress.lng),
    } as any);
    setShipping(rowCosts[deliveryAddress.id] ?? 0);
  };

  const handleAddNewAddress = () => {
    setSelectedAddressId(null);
    setAddress("");
    setLocation(undefined as any);
    setShipping(0);
    setNewAddressName("");
    setLastMapQuote(null);
    setAddressMode("map");
  };

  const handleOk = async () => {
    console.log("🔵 [handleOk] Iniciando handleOk");
    console.log("🔵 [handleOk] disableSend actual:", disableSend);

    if (shippedType === "") {
      console.log("❌ [handleOk] Error: No se seleccionó tipo de envío");
      CommonService.displayAlert("Debe seleccionar un tipo de envío");
      return;
    }
    if (phone === "" && shippedType === "shipped") {
      console.log("❌ [handleOk] Error: Teléfono requerido para domicilio");
      CommonService.displayAlert("El teléfono es requerido");
      return;
    }
    if (name === "") {
      console.log("❌ [handleOk] Error: Nombre requerido");
      CommonService.displayAlert("El nombre es requerido");
      return;
    }

    // Validar que la fecha no sea en el pasado
    if (time) {
      const selectedDate = new Date(time);
      const now = new Date();

      if (selectedDate < now) {
        console.log("❌ [handleOk] Error: Fecha en el pasado");
        CommonService.displayAlert("No puedes agendar un pedido en una fecha pasada. Por favor verifica el año y la fecha seleccionada.");
        return;
      }
    }

    if (isLocal) {
      if (address === "") {
        console.log("❌ [handleOk] Error: Dirección requerida");
        CommonService.displayAlert("La dirección es requerida");
        return;
      }
    }

    console.log("✅ [handleOk] Validaciones pasadas");
    setDisableSend(true);

    // El cliente ya fue identificado por el gate de CustomerIdentification
    // (nombre/teléfono ya no se editan aquí). Solo se refresca su dirección
    // por default si se capturó una nueva en este pedido.
    if (isRealCustomer && address !== "" && address !== customer.direccion) {
      await CustomerService.updateCustomerById(customer.id, {
        telefono: phone,
        direccion: address,
        direccion2: dir2,
      });
    }

    // Si se capturó una dirección nueva por el mapa (no una ya guardada), se
    // guarda para que la próxima vez aparezca en la lista con su costo cacheado.
    if (
      isRealCustomer &&
      shippedType === "shipped" &&
      addressMode === "map" &&
      location &&
      (location as any).lat &&
      (location as any).lng
    ) {
      const savedAddress = await createAddress({
        name: newAddressName || "Dirección",
        address,
        number: "S/N",
        lat: (location as any).lat,
        lng: (location as any).lng,
      });
      if (savedAddress && lastMapQuote) {
        // Se cachea exactamente el precio BASE + distancia que <Map> ya
        // calculó para la cotización en vivo (no `shipping`, que trae el
        // descuento de esta orden, ni un recálculo aparte que podría dar
        // un número distinto por timing de la config).
        cacheShippingCost(
          savedAddress.id,
          user.warehouse_id,
          lastMapQuote.rawPrice,
          lastMapQuote.distanceMeters,
          lastMapQuote.durationSeconds
        );
      }
    }

    createSale(customer, shipping, shippedType, state, location, time);

    console.log("🔵 [handleOk] Limpiando campos del formulario");
    setPhone("");
    setAddress("");
    setDir2("");
    setName("");
    setIsLocal(false);
    setShippedType("");
    setShipping(0);
    setShowDir(false);
    setTime("");
    setSelectedAddressId(null);
    setNewAddressName("");
    setLastMapQuote(null);
    setAddressMode("map");
    console.log("🔵 [handleOk] handleOk completado");
  };

  const createSale = (ncustomer?: ICustomer, shipping?: number, shippedType?: string | undefined, state?: { checkDir: boolean; checkDir2: boolean; } | undefined, location?: any, time?: string | undefined) => {
    console.log("🟡 [createSale] Iniciando createSale");
    console.log("🟡 [createSale] Estableciendo disableSend = true");
    setDisableSend(true);

    const auxOrder = products.some(needsOrderFlow);

    // Filtrar las líneas de servicio de asado - el backend las agregará automáticamente
    const productsToSend = products
      .filter((product: IProduct) => !product.is_grilling_service)
      .map(toSalePayload);

    console.log('📤 Productos a enviar al backend (orderOptions):', productsToSend.map((p) => ({
      id: p.id,
      name: p.name,
      grilling_config: p.grilling_config
    })));

    // Calcular el total con envío
    const shippingCost = shipping ?? 0;
    const totalWithShipping = Number(order.total) + shippingCost;

    // Actualizar los pagos para incluir el costo de envío
    const updatedPayments = pays.map((payment: IPayment) => {
      // Solo actualizar pagos por transferencia o crédito que no sean anticipos
      if ((payment.payment_method === PaymentsMethods.Transfer || payment.payment_method === PaymentsMethods.Credit) && !payment.is_anticipo) {
        console.log(`🟡 [createSale] Actualizando pago ${payment.payment_method} de $${payment.amount} a $${totalWithShipping.toFixed(2)} (incluyendo envío de $${shippingCost})`);
        return {
          ...payment,
          amount: Number(totalWithShipping.toFixed(2))
        };
      }
      return payment;
    });

    const data: TRequestSale = {
      orderProducts: productsToSend,
      total: order.total,
      subtotal: order.subtotal,
      iva: order.iva,
      ieps: order.ieps,
      descuento: order.discount,
      metodoPago: "other",
      shipping: shipping ?? 0,
      notes_order: order.notes_order,
      schedule: time,
      cliente: ncustomer ?? customer,
      sale_type: "Order",
      payments: updatedPayments,
      user_id: user.id,
      warehouse_id: user.warehouse_id,
      cash_received: 0, //parseFloat(amountReceived),
      cashBox: user.cbox,
      notes: address,
      state,
      location,
      shipped_type: shippedType,
    };

    console.log("🟡 [createSale] Enviando petición al backend...");
    SaleService.createSalePos(data).then((res) => {
      console.log("🟡 [createSale] Respuesta recibida del backend:", res.success);
      if (res.success) {
        console.log("✅ [createSale] Venta creada exitosamente");
        const saleId = res.object?.id ?? 0;
        const paymentTransfer = updatedPayments.find(
          (payment: IPayment) => payment.payment_method === PaymentsMethods.Transfer && !payment.is_anticipo)
        const paymentCredit = updatedPayments.find(
          (payment: IPayment) => payment.payment_method === PaymentsMethods.Credit);

        // Calcular el total pagado (solo pagos aprobados)
        const totalPaid = updatedPayments
          .filter((payment: IPayment) => payment.status === "approved")
          .reduce((sum: number, payment: IPayment) => sum + payment.amount, 0);
        const orderTotal = Number(order.total) + (shipping ?? 0);
        const isFullyPaid = totalPaid >= orderTotal;

        if (paymentCredit) {
          console.log("🟡 [createSale] Pago a crédito detectado, solicitando autorización");
          const idAuth = v4();
          CommonService.requestAuthorize({
            message: `Se solicita Autorizar un pago a Crédito de $ ${paymentCredit.amount.toFixed(
              2
            )}`, // => $req->message,
            action: {
              type: "authorize-payment",
              item: { order_id: saleId },
              id: idAuth,
              content: `Se solicita Autorizar un pago a Crédito de $ ${paymentCredit.amount.toFixed(
                2
              )}`,
            }, // => $req->action,
            status: "pending", // => 'pending', // Set the initial status
            from: user.id, // => $req->from,
            to: 1, // => $req->to,
          }).then(() => {
            console.log("🟡 [createSale] Autorización solicitada, cerrando modal");
            CommonService.toast(
              "Se envío solicitud de Autorización, para validar pago a Crédito",
              "success"
            );
            modalManager.closeModal();
          });
        } else if (paymentTransfer) {
          console.log("🟡 [createSale] Pago por transferencia detectado, solicitando autorización");
          const idAuth = v4();
          CommonService.requestAuthorize({
            message: `Se solicita Autorizar un pago por Transferencia de $ ${paymentTransfer.amount.toFixed(2)}`, // => $req->message,
            action: {
              type: "authorize-payment",
              item: { order_id: saleId },
              id: idAuth,
              content: `Se solicita Autorizar un pago por Transferencia de $ ${paymentTransfer.amount.toFixed(
                2
              )}`,
            }, // => $req->action,
            status: "pending", // => 'pending', // Set the initial status
            from: user.id, // => $req->from,
            to: 1, // => $req->to,
          }).then(() => {
            console.log("🟡 [createSale] Autorización solicitada, cerrando modal");
            CommonService.toast("Se envío solicitud de Autorización, para validar pago por transferencia", "success")
            modalManager.closeModal();
          });
          // pusher.
        } else if (isFullyPaid) {
          // Solo imprimir ticket si el pedido está completamente pagado
          CommonService.printTicketSale(saleId)
            .then(() => {
              console.log("printing");
              if (auxOrder) {
                CommonService.printTicketOrder(saleId)
                  .then(() => {
                    console.log("printing Comanda");
                  })
                  .catch((err) => {
                    console.log(err);
                  });
              }
            })
            .catch((err) => {
              console.log(err);
            });
          console.log("🟡 [createSale] Cerrando modal y mostrando mensaje de éxito");
          modalManager.closeModal()
          MySwal.fire({
            icon: "success",
            showConfirmButton: false,
            timer: 1500,
          });
        } else {
          console.log("🟡 [createSale] Pedido con pago pendiente, cerrando modal");
          // Pedido registrado sin pago completo - NO imprimir ticket
          modalManager.closeModal()
          MySwal.fire({
            icon: "success",
            title: "Pedido registrado",
            text: isFullyPaid ? undefined : "El pedido se registró con pago pendiente",
            showConfirmButton: false,
            timer: 1500,
          });
        }
        console.log("🟡 [createSale] Limpiando orden");
        clearOrder();
        // setOpenModal(false)
      } else {
        console.log("❌ [createSale] Error al crear venta:", res.message);
        CommonService.toast(res.message, "error");
      }
      console.log("⚠️ [createSale] ADVERTENCIA: Estableciendo disableSend = false al final de createSale");
      setDisableSend(false);
    });
  };

  useEffect(() => {
    if (shippedType === "onSite" || shippedType === "inRoom") {
      setIsLocal(false);
      setShipping(0);
    } else {
      setIsLocal(true);
    }
    if (shippedType !== "") setShowDir(true);
  }, [shippedType]);

  // Al entrar a "Domicilio", si el cliente ya tiene direcciones guardadas se
  // muestra la lista; si no tiene ninguna, se va directo al mapa.
  useEffect(() => {
    if (shippedType === "shipped" && isRealCustomer && addresses.length > 0) {
      setAddressMode("list");
    } else {
      setAddressMode("map");
    }
  }, [shippedType, isRealCustomer, addresses.length]);

  const [state, setState] = React.useState({
    checkDir: true,
    checkDir2: false,
  });
  useEffect(() => {
    console.log("🔄 [useEffect] Componente montado/actualizado");
    // Precarga siempre: cliente real identificado, o "Público en General"
    // con el nombre que se haya tecleado para identificar el pedido.
    if (customer) {
      setPhone(customer.telefono ?? "");
      setName(customer.nombre ?? "");
      setAddress(isRealCustomer ? customer.direccion ?? "" : "");
      setDir2(customer.direccion2 ?? "");
    }

    // Reset del cliente identificado al desmontar, sin importar CÓMO se cerró
    // el modal (botón Cancelar, venta exitosa, o cualquier otro camino que
    // cambie modalManager.component) — a diferencia de handleCancel/clearOrder,
    // que solo corren si se disparó ESE botón/flujo específico, esto corre
    // siempre que el componente deje de estar montado.
    return () => {
      setCustomer(posInitialState.customer);
    };
  }, [])

  return (
    <>
      <Dialog
        sx={{ "& .MuiDialog-paper": { width: "80%", maxHeight: 800 } }}
        maxWidth="lg"
        TransitionProps={{ onEntering: handleEntering }}
        open={true}      // open={open}
        // {...other}
      >
        <DialogTitle>Seleccione tipo de Envío</DialogTitle>
        <DialogActions style={{ display: "flex", justifyContent: "center" }}>
          <ToggleButtonGroup
            color="primary"
            aria-label="outlined primary button group"
            exclusive
            value={shippedType}
            onChange={(e: any, value: string) => setShippedType(value)}
            disabled={disableSend}
          >
            <ToggleButton value="onSite" disabled={disableSend}>Recoger en Tienda</ToggleButton>
            <ToggleButton value="inRoom" disabled={disableSend}>Comedor</ToggleButton>
            <ToggleButton value="shipped" disabled={disableSend}>Domicilio</ToggleButton>
          </ToggleButtonGroup>
        </DialogActions>
        {showDir && (
          <div style={{ marginLeft: "30px", marginRight: "30px" }}>
            <TextField
              margin="normal"
              fullWidth
              name="phone"
              label="Telefono"
              type="phone"
              id="phone"
              value={phone}
              disabled
            />
            <TextField
              margin="normal"
              fullWidth
              name="name"
              label="Nombre"
              type="name"
              id="name"
              value={name}
              disabled
            />
            <TextField
              margin="normal"
              fullWidth
              name="name"
              label="Fecha y Hora"
              type="datetime-local"
              id="name"
              value={time}
              InputLabelProps={{
                shrink: true,
              }}
              onChange={(e) => setTime(e.target.value)}
              onFocus={(e) => {
                const target = e.target as HTMLInputElement;
                if (target.showPicker) {
                  target.showPicker(); // Safely call the showPicker method
                }
              }}
              disabled={disableSend}
            />
            {shippedType === "shipped" && addressMode === "list" && (
              <div style={{ marginTop: "10px" }}>
                <div style={{ display: "flex", alignItems: "center", padding: "0 16px", opacity: 0.7 }}>
                  <div style={{ width: "42px" }} />
                  <div style={{ flex: 1 }}>Dirección</div>
                  <div style={{ minWidth: "70px", textAlign: "right" }}>Km</div>
                  <div style={{ minWidth: "70px", textAlign: "right" }}>Tiempo</div>
                  <div style={{ minWidth: "90px", textAlign: "right" }}>Envío</div>
                  <div style={{ minWidth: "90px", textAlign: "right" }}>Total</div>
                </div>
                <List>
                  {addresses.map((deliveryAddress) => {
                    const shippingCost = rowCosts[deliveryAddress.id];
                    const meta = rowMeta[deliveryAddress.id];
                    const isCalculating = calculatingIds[deliveryAddress.id];
                    return (
                      <ListItemButton
                        key={deliveryAddress.id}
                        selected={selectedAddressId === deliveryAddress.id}
                        onClick={() => handleSelectAddress(deliveryAddress)}
                        disabled={disableSend || isCalculating}
                        style={{ display: "flex", alignItems: "center" }}
                      >
                        <Radio checked={selectedAddressId === deliveryAddress.id} />
                        <ListItemText
                          primary={
                            <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                              {deliveryAddress.name}
                              <IconButton
                                size="small"
                                title="Ver en el mapa"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setPreviewAddress(deliveryAddress);
                                }}
                              >
                                <PlaceIcon fontSize="small" />
                              </IconButton>
                            </span>
                          }
                          secondary={deliveryAddress.address}
                        />
                        <div style={{ minWidth: "70px", textAlign: "right" }}>
                          {isCalculating ? (
                            <CircularProgress size={18} />
                          ) : meta !== undefined ? (
                            `${(meta.distanceMeters / 1000).toFixed(2)} km`
                          ) : (
                            "-"
                          )}
                        </div>
                        <div style={{ minWidth: "70px", textAlign: "right" }}>
                          {isCalculating ? (
                            <CircularProgress size={18} />
                          ) : meta?.durationSeconds !== undefined ? (
                            `${Math.round(meta.durationSeconds / 60)} min`
                          ) : (
                            "-"
                          )}
                        </div>
                        <div style={{ minWidth: "90px", textAlign: "right" }}>
                          {isCalculating ? (
                            <CircularProgress size={18} />
                          ) : shippingCost !== undefined ? (
                            shippingCost === 0 ? (
                              <span style={{ color: "#22c55e", fontWeight: 600 }}>Gratis</span>
                            ) : (
                              `$ ${shippingCost.toFixed(2)}`
                            )
                          ) : (
                            "-"
                          )}
                        </div>
                        <div style={{ minWidth: "90px", textAlign: "right" }}>
                          {!isCalculating && shippingCost !== undefined
                            ? `$ ${(shippingCost + Number(order.total ?? 0)).toFixed(2)}`
                            : "-"}
                        </div>
                      </ListItemButton>
                    );
                  })}
                </List>
                <Divider style={{ margin: "10px 0" }} />
                <Button variant="outlined" onClick={handleAddNewAddress} disabled={disableSend}>
                  + Agregar dirección nueva
                </Button>
              </div>
            )}
            {shippedType === "shipped" && addressMode === "map" && (
              <>
                {isRealCustomer && (
                  <>
                    <TextField
                      margin="normal"
                      fullWidth
                      name="newAddressName"
                      label="Nombre de la dirección (ej. Casa, Oficina)"
                      id="newAddressName"
                      value={newAddressName}
                      onChange={(e) => setNewAddressName(e.target.value)}
                      disabled={disableSend}
                    />
                    {addresses.length > 0 && (
                      <Button
                        variant="text"
                        onClick={() => setAddressMode("list")}
                        disabled={disableSend}
                        style={{ marginBottom: "10px" }}
                      >
                        Volver a mis direcciones
                      </Button>
                    )}
                  </>
                )}
                <Map
                  setAddress={setAddress}
                  setLocation={setLocation}
                  orderTotal={Number(order.total ?? 0)}
                  setShipping={setShipping}
                  address={address}
                  defaultPoint={{
                    lat: Number(user.user.warehouse.lat),
                    lng: Number(user.user.warehouse.lng),
                  }}
                  warehouseId={user.user.warehouse.id}
                  onQuote={(rawPrice, distanceMeters, durationSeconds) =>
                    setLastMapQuote({ rawPrice, distanceMeters, durationSeconds })
                  }
                />
              </>
            )}
          </div>
        )}
        <DialogActions style={{ display: "flex", justifyContent: "center" }}>
          <Button
            autoFocus
            onClick={handleCancel}
            variant="contained"
            color="error"
            style={{ marginRight: "20px" }}
            disabled={disableSend}
          >
            Cancel
          </Button>
          <Button
            onClick={handleOk}
            variant="contained"
            color="success"
            style={{ color: "#fff" }}
            disabled={disableSend}
          >
            {disableSend ? (
              <>
                <CircularProgress size={20} style={{ marginRight: "10px", color: "#fff" }} />
                Procesando...
              </>
            ) : (
              "Ok"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={previewAddress !== null} onClose={() => setPreviewAddress(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {previewAddress?.name}
          {user?.user?.warehouse && (
            <div style={{ fontSize: "0.75rem", opacity: 0.7, fontWeight: 400 }}>
              Círculo centrado en: {user.user.warehouse.name ?? `id ${user.user.warehouse.id}`} (
              {Number(user.user.warehouse.lat).toFixed(5)}, {Number(user.user.warehouse.lng).toFixed(5)}) — usando
              warehouse_id {user.warehouse_id} para el costo cacheado
            </div>
          )}
        </DialogTitle>
        {previewAddress && mapsLoaded && (
          <GoogleMap
            mapContainerStyle={{ width: "100%", height: "400px" }}
            center={{ lat: Number(previewAddress.lat), lng: Number(previewAddress.lng) }}
            zoom={16}
            onLoad={(map) => {
              // Centrar el zoom en la dirección puede dejar el círculo (centrado en la
              // sucursal) fuera de vista si están lejos entre sí — se encuadran ambos.
              if (!user?.user?.warehouse) return;
              const bounds = new google.maps.LatLngBounds();
              bounds.extend({ lat: Number(previewAddress.lat), lng: Number(previewAddress.lng) });
              const warehouseCenter = new google.maps.LatLng(
                Number(user.user.warehouse.lat),
                Number(user.user.warehouse.lng)
              );
              if (perimeterPoints.length > 0) {
                perimeterPoints.forEach((p) => bounds.extend(p));
              } else {
                const circleBounds = new google.maps.Circle({
                  center: warehouseCenter,
                  radius: getFreeShippingRadius(shippingConfig),
                }).getBounds();
                if (circleBounds) bounds.union(circleBounds);
              }
              map.fitBounds(bounds, 40);
            }}
          >
            <Marker position={{ lat: Number(previewAddress.lat), lng: Number(previewAddress.lng) }} />
            {!configLoading &&
              user?.user?.warehouse &&
              (perimeterPoints.length > 0 ? (
                <Polygon
                  paths={perimeterPoints}
                  options={{
                    strokeColor: "#22c55e",
                    strokeOpacity: 0.8,
                    strokeWeight: 2,
                    fillColor: "#22c55e",
                    fillOpacity: 0.15,
                    clickable: false,
                  }}
                />
              ) : (
                <Circle
                  center={{ lat: Number(user.user.warehouse.lat), lng: Number(user.user.warehouse.lng) }}
                  radius={getFreeShippingRadius(shippingConfig)}
                  options={{
                    strokeColor: "#22c55e",
                    strokeOpacity: 0.8,
                    strokeWeight: 2,
                    fillColor: "#22c55e",
                    fillOpacity: 0.15,
                    clickable: false,
                  }}
                />
              ))}
          </GoogleMap>
        )}
        {previewAddress && (
          <div style={{ display: "flex", justifyContent: "space-around", padding: "16px", textAlign: "center" }}>
            <div>
              <div style={{ opacity: 0.7, fontSize: "0.8rem" }}>Distancia</div>
              <div>
                {rowMeta[previewAddress.id] !== undefined
                  ? `${(rowMeta[previewAddress.id].distanceMeters / 1000).toFixed(2)} km`
                  : "-"}
              </div>
            </div>
            <div>
              <div style={{ opacity: 0.7, fontSize: "0.8rem" }}>Tiempo</div>
              <div>
                {rowMeta[previewAddress.id]?.durationSeconds !== undefined
                  ? `${Math.round(rowMeta[previewAddress.id].durationSeconds! / 60)} min`
                  : "-"}
              </div>
            </div>
            <div>
              <div style={{ opacity: 0.7, fontSize: "0.8rem" }}>Envío</div>
              <div>
                {rowCosts[previewAddress.id] !== undefined ? (
                  rowCosts[previewAddress.id] === 0 ? (
                    <span style={{ color: "#22c55e", fontWeight: 600 }}>Gratis</span>
                  ) : (
                    `$ ${rowCosts[previewAddress.id].toFixed(2)}`
                  )
                ) : (
                  "-"
                )}
              </div>
            </div>
          </div>
        )}
        {previewAddress && rowMeta[previewAddress.id] !== undefined && !configLoading && (
          <div style={{ textAlign: "center", paddingBottom: "12px", fontSize: "0.85rem" }}>
            {(() => {
              const radius = getFreeShippingRadius(shippingConfig);
              const distance = rowMeta[previewAddress.id].distanceMeters;
              const diff = Math.abs(distance - radius);
              return distance <= radius ? (
                <span style={{ color: "#22c55e" }}>
                  Dentro del radio de {radius}m — {diff.toFixed(0)}m antes del límite
                </span>
              ) : (
                <span style={{ color: "#ef4444" }}>
                  Fuera del radio de {radius}m — {diff.toFixed(0)}m más allá del límite
                </span>
              );
            })()}
          </div>
        )}
        <DialogActions>
          <Button onClick={() => setPreviewAddress(null)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* Backdrop para bloquear interacción durante el procesamiento */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.modal + 1 }}
        open={disableSend}
      >
        <div style={{ textAlign: "center" }}>
          <CircularProgress color="inherit" size={60} />
          <div style={{ marginTop: "20px", fontSize: "18px" }}>
            Procesando venta...
          </div>
        </div>
      </Backdrop>
    </>
  )
}
